import { context, trace, type Span } from "@opentelemetry/api";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { Resource } from "@opentelemetry/resources";
import {
  BatchSpanProcessor,
  TraceIdRatioBasedSampler,
  WebTracerProvider,
} from "@opentelemetry/sdk-trace-web";
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from "@opentelemetry/semantic-conventions";

/**
 * Browser-side OpenTelemetry for the HRMS SPA.
 *
 * What this gives you in the trace backend that backend tracing alone cannot: the time
 * between a user clicking and the API being called (render, bundle parse,
 * queued requests), and failures that never reach the backend at all -
 * a CORS rejection, a DNS failure, an offline device. A backend trace of a
 * request that was never sent does not exist.
 *
 * Spans are sent to the SAME collector the backend uses, over OTLP/HTTP,
 * proxied through this app's own origin (see `/v1/traces` in
 * frontend/nginx.conf). The browser therefore never learns the collector's
 * address and the collector is never published to the internet.
 *
 * ## What is deliberately NOT captured
 *
 * No form values, no request or response BODIES, no headers, no click or
 * keystroke instrumentation. The instrumentations enabled below record
 * timing, URL, and status - nothing that could carry a password, a token, an
 * employee's personal details or a payroll figure. `applyCustomAttributes`
 * is not used on either HTTP instrumentation precisely because it is the
 * hook that would hand a callback the response object.
 *
 * URLs are sanitised on the way out (see `scrubUrl`) so a query string can
 * never carry a token into telemetry even if some future call site puts one
 * there.
 *
 * ## Failure behaviour
 *
 * Every failure mode here is silent and non-fatal. A collector that is down,
 * a blocked request, a browser extension breaking XHR patching - none of it
 * may break the HRMS UI, so `startTelemetry` never throws and never returns
 * a rejected promise. Telemetry is disabled entirely unless
 * VITE_OTEL_EXPORTER_URL is set, which keeps `npm run dev` and the test
 * suite free of it by default.
 */

const SERVICE_NAME = import.meta.env.VITE_OTEL_SERVICE_NAME || "hrms-web";

/** Spans are dropped client-side at this ratio before anything is sent. */
function sampleRatio(): number {
  const raw = Number.parseFloat(import.meta.env.VITE_OTEL_SAMPLE_RATIO ?? "");
  if (Number.isNaN(raw) || raw < 0 || raw > 1) return 1;
  return raw;
}

/**
 * Query-string keys whose value must never reach telemetry. Matches the
 * backend's own list in internal/platform/tracing/sanitize.go - the two are
 * separate implementations of one policy, so both are kept short and
 * obvious rather than clever.
 */
const SENSITIVE_QUERY_KEYS = [
  "token",
  "access_token",
  "refresh_token",
  "api_key",
  "apikey",
  "key",
  "secret",
  "password",
  "signature",
  "code",
];

/**
 * The placeholder written over a redacted query parameter.
 *
 * Deliberately unbracketed, unlike the backend's `[REDACTED]`: this value
 * goes through URLSearchParams, which percent-encodes brackets into
 * `%5BREDACTED%5D` - technically correct and unreadable in a trace span
 * list. The two placeholders differ because the two contexts encode
 * differently, not by oversight.
 */
const REDACTED = "REDACTED";

/**
 * Strips sensitive query parameters from a URL before it becomes a span
 * attribute. Returns the input unchanged if it cannot be parsed - a URL this
 * function does not understand is left alone rather than guessed at, and the
 * collector's own scrubbing processor is the backstop for that case.
 */
export function scrubUrl(raw: string): string {
  try {
    const url = new URL(raw, window.location.origin);
    let touched = false;
    for (const key of SENSITIVE_QUERY_KEYS) {
      if (url.searchParams.has(key)) {
        url.searchParams.set(key, REDACTED);
        touched = true;
      }
    }
    return touched ? url.toString() : raw;
  } catch {
    return raw;
  }
}

let provider: WebTracerProvider | undefined;

/**
 * Initialises browser telemetry. Safe to call more than once; the second
 * call is a no-op.
 *
 * Called from main.tsx BEFORE React renders, so the document-load
 * instrumentation still sees the navigation timings and the XHR patch is in
 * place before the first API call goes out.
 */
export function startTelemetry(): void {
  const endpoint = import.meta.env.VITE_OTEL_EXPORTER_URL;
  if (!endpoint || provider) return;

  try {
    // Only same-origin API calls get a traceparent header. A third-party
    // URL must never receive one: it would leak this deployment's internal
    // trace ids to an unrelated server, and the preflight it triggers would
    // break requests that work fine today.
    const propagateTo = [new RegExp(`^${escapeRegExp(window.location.origin)}`)];

    provider = new WebTracerProvider({
      resource: new Resource({
        [ATTR_SERVICE_NAME]: SERVICE_NAME,
        [ATTR_SERVICE_VERSION]: import.meta.env.VITE_APP_VERSION || "dev",
        "deployment.environment": import.meta.env.MODE,
      }),
      sampler: new TraceIdRatioBasedSampler(sampleRatio()),
      spanProcessors: [
        new BatchSpanProcessor(new OTLPTraceExporter({ url: endpoint }), {
          // Small and frequent: a browser tab can be closed at any moment,
          // and anything still sitting in the queue is lost. Five seconds
          // bounds how much of a session a hard close can take with it.
          scheduledDelayMillis: 5000,
          maxQueueSize: 100,
          maxExportBatchSize: 20,
        }),
      ],
    });

    provider.register();

    registerInstrumentations({
      tracerProvider: provider,
      instrumentations: [
        // Page load timing: DNS, TLS, TTFB, DOM interactive, load. This is
        // the "page/navigation performance" signal, and it comes from the
        // browser's own PerformanceTiming rather than from anything this
        // app has to measure itself.
        new DocumentLoadInstrumentation(),

        // axios uses XHR in the browser, so this is the one that actually
        // instruments the HRMS API layer (src/lib/api/client.ts). Fetch is
        // instrumented too for the handful of direct fetch() call sites.
        new XMLHttpRequestInstrumentation({
          propagateTraceHeaderCorsUrls: propagateTo,
          // Static assets are served by nginx off the same origin and would
          // otherwise triple the span volume while telling us nothing the
          // document-load timings do not.
          ignoreUrls: [/\/assets\//, /\.(?:js|css|png|jpe?g|svg|woff2?)$/],
        }),
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls: propagateTo,
          ignoreUrls: [/\/assets\//, /\.(?:js|css|png|jpe?g|svg|woff2?)$/],
        }),
      ],
    });

    installErrorHandlers();
  } catch {
    // A telemetry setup failure must never stop the app from rendering.
    provider = undefined;
  }
}

/**
 * Flushes pending spans. Called on pagehide so a user navigating away does
 * not silently discard the trace of whatever they were doing - which is
 * disproportionately likely to be the failure they are about to report.
 */
export function shutdownTelemetry(): void {
  void provider?.forceFlush().catch(() => undefined);
}

/**
 * Records an unhandled error as its own span, marked failed.
 *
 * A separate span rather than an event on whatever span happens to be
 * active: a JS error usually surfaces in an event handler with no active
 * span at all, and attaching it to an unrelated one would misattribute it.
 */
export function recordError(error: unknown, kind: string): void {
  if (!provider) return;
  try {
    const tracer = trace.getTracer(SERVICE_NAME);
    const span = tracer.startSpan(`browser.error.${kind}`, {}, context.active());
    applyError(span, error);
    span.end();
  } catch {
    // ignore - reporting an error must not raise one
  }
}

function applyError(span: Span, error: unknown): void {
  const err = error instanceof Error ? error : new Error(String(error));
  span.recordException({
    name: err.name,
    message: err.message,
    // The stack is the whole point of an error span. It contains file and
    // line references from this app's own bundle - no user data.
    stack: err.stack,
  });
  span.setStatus({ code: 2, message: err.message }); // 2 = SpanStatusCode.ERROR
  span.setAttribute("browser.url", scrubUrl(window.location.href));
}

function installErrorHandlers(): void {
  window.addEventListener("error", (event) => {
    recordError(event.error ?? event.message, "uncaught");
  });
  window.addEventListener("unhandledrejection", (event) => {
    recordError(event.reason, "unhandled_rejection");
  });
  // pagehide rather than unload: unload does not fire on mobile Safari or
  // when a tab is restored from the bfcache, which is most of the cases
  // where a flush matters.
  window.addEventListener("pagehide", shutdownTelemetry);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
